import React from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import HeroCarousel from "../components/HeroCarousel";
import {
    Package,
    FileText,
    GraduationCap,
    Bell,
    Phone,
    MessageSquare,
    ArrowUpRight,
    ShieldCheck,
    Truck,
    MapPin,
} from "lucide-react";
import { useQuote } from "../context/QuoteContext";
import { nextCourseDateLabel } from "../lib/nextCourseDate";

const HomePage = () => {
    const { items } = useQuote();

    const openChat = () => {
        document.querySelector('[data-testid="chat-trigger"]')?.click();
    };

    return (
        <AppLayout pageTitle="NSP Noleggio">
            {/* ============ HERO CAROUSEL ============ */}
            <HeroCarousel />

            {/* ============ HUB CARDS ============ */}
            <section
                data-testid="home-hub"
                className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14 py-14 md:py-20"
            >
                <div className="mb-10 lg:mb-14 max-w-2xl">
                    <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-nsp-green">
                        Le nostre sezioni
                    </div>
                    <h2 className="mt-3 font-display uppercase text-3xl md:text-4xl lg:text-5xl tracking-tight text-nsp-green leading-none">
                        Cosa vuoi fare?
                    </h2>
                    <p className="mt-3 text-gray-500">
                        Scegli la sezione che ti interessa: ogni percorso è pensato per essere
                        rapido e senza complicazioni.
                    </p>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-5">
                    {/* PREVENTIVO — primo posto */}
                    <HubCardTinted
                        to="/preventivo"
                        title="Preventivo Noleggio"
                        subtitle="Calcolo istantaneo con PDF"
                        icon={FileText}
                        span="col-span-1 sm:col-span-2 lg:col-span-2"
                        variant="yellow"
                        image="/hero/preventivo-doc.png"
                        badge={items.length > 0 ? `${items.length} nel carrello` : null}
                    />

                    {/* CATALOGO — secondo posto */}
                    <HubCardPhoto
                        to="/catalogo"
                        title="Consulta il Catalogo Mezzi"
                        subtitle="Oltre 60 attrezzature: escavatori, gruppi elettrogeni, ponteggi..."
                        image="/hero/catalogo-pagina.png"
                        chip="60+ MEZZI"
                        icon={Package}
                        span="col-span-1 sm:col-span-2 lg:col-span-4 lg:row-span-2"
                        variant="dark"
                    />

                    {/* CORSI — banner con grafica */}
                    <HubCardTinted
                        to="/corsi"
                        title="Corsi & Patentini"
                        subtitle="Formazione D.Lgs. 81/08 per operatori di macchine e mezzi"
                        icon={GraduationCap}
                        span="col-span-1 sm:col-span-2 lg:col-span-2"
                        variant="gray"
                        image="/hero/corsi-attestato.png"
                        badge="Attestato incluso"
                        highlight={`Prossimo corso: ${nextCourseDateLabel()}`}
                        chips={["Escavatori", "Gru scarrabili", "Ponteggi"]}
                    />

                    {/* CHAT AI — card ampia e invitante */}
                    <ChatAssistantCard onClick={openChat} />

                    {/* NOTIFICHE */}
                    <HubCardTinted
                        to="/notifiche"
                        title="Notifiche"
                        subtitle="Offerte e novità"
                        icon={Bell}
                        span="col-span-1 lg:col-span-3"
                        variant="light"
                    />

                    {/* CONTATTI */}
                    <HubCardTinted
                        to="/contatti"
                        title="Contatti"
                        subtitle="Chi siamo, sede, mappa"
                        icon={Phone}
                        span="col-span-1 lg:col-span-3"
                        variant="light"
                    />
                </div>
            </section>

            {/* ============ COMFORT STRIP ============ */}
            <section className="bg-nsp-green-50/50 border-y border-nsp-green-100">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14 py-10">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <ComfortCard
                            icon={Truck}
                            title="Consegna in cantiere"
                            text="Trasporto in tutta la Sabina, tariffe trasparenti."
                        />
                        <ComfortCard
                            icon={ShieldCheck}
                            title="Mezzi certificati"
                            text="Manutenzione programmata, sicurezza sempre."
                        />
                        <ComfortCard
                            icon={MapPin}
                            title="Sede unica"
                            text="Via Salaria Vecchia 31, Osteria Nuova di Poggio Moiano (RI)."
                            href="/contatti"
                        />
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

/* ============ SUB-COMPONENTS ============ */

/* Card ampia dell'assistente AI: invita a chattare per info, prezzi e preventivi. */
const ChatAssistantCard = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid="hub-card-chat"
        className="group relative overflow-hidden rounded-3xl p-6 md:p-8 col-span-1 sm:col-span-2 lg:col-span-6 min-h-[200px] flex flex-col justify-between text-left bg-gradient-to-br from-nsp-green via-nsp-green-800 to-nsp-green text-white ring-1 ring-black/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_20px_50px_-15px_rgba(10,59,37,0.6)] hover:-translate-y-1 transition-transform"
    >
        <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-nsp-yellow/10 blur-2xl pointer-events-none" />
        <div className="relative z-10 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-nsp-yellow text-nsp-green flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6" strokeWidth={2.2} />
                </div>
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Online adesso · 24/7
                </span>
            </div>
            <ArrowUpRight className="w-5 h-5 opacity-30 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-300" />
        </div>
        <div className="relative z-10 mt-6 grid md:grid-cols-[1fr_auto] gap-5 md:items-end">
            <div>
                <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-none">
                    Chatta con il nostro <span className="text-nsp-yellow">assistente</span>
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/75 leading-relaxed max-w-2xl">
                    Hai dubbi su quale mezzo scegliere, sui prezzi o sulla durata più conveniente?
                    Scrivici in chat: l'assistente NSP ti risponde subito e può persino
                    aggiungere i prodotti al tuo preventivo.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {["Info prezzi e tariffe", "Consigli sul mezzo giusto", "Prepara il preventivo", "Corsi e patentini"].map((c) => (
                        <span
                            key={c}
                            className="inline-flex bg-white/10 border border-white/15 text-white/85 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                        >
                            {c}
                        </span>
                    ))}
                </div>
            </div>
            <span className="inline-flex items-center justify-center gap-2 self-start md:self-end rounded-full bg-nsp-yellow text-nsp-green px-6 py-3.5 font-bold uppercase tracking-widest text-xs shadow-md group-hover:bg-white transition-colors">
                <MessageSquare className="w-4 h-4" /> Inizia la chat
            </span>
        </div>
    </button>
);

/* Card grande con foto prodotto in bleed. Sfondo verde scuro per contrasto,
   usata per il "protagonista" della griglia (Catalogo). */
const HubCardPhoto = ({ to, title, subtitle, image, chip, icon: Icon, span, variant }) => {
    const bgClass =
        variant === "dark"
            ? "bg-gradient-to-br from-nsp-green via-nsp-green-800 to-nsp-green text-white"
            : "bg-white text-nsp-green";
    return (
        <Link
            to={to}
            data-testid={`hub-card-${to.slice(1)}`}
            className={`group relative overflow-hidden rounded-3xl p-6 md:p-8 min-h-[220px] md:min-h-[380px] flex flex-col justify-between ring-1 ring-black/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-transform ${bgClass} ${span}`}
        >
            {/* Chip */}
            {chip && (
                <div className="relative z-10 inline-flex self-start bg-nsp-yellow text-nsp-green text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
                    {chip}
                </div>
            )}

            {/* Product image bleeding out */}
            <div className="absolute -right-3 -bottom-12 w-44 max-w-[42%] h-64 md:-right-8 md:-bottom-28 md:w-[400px] md:max-w-[52%] md:h-[560px] pointer-events-none">
                <img
                    src={image}
                    alt=""
                    className="w-full h-full object-contain object-top group-hover:scale-105 transition-transform duration-700"
                />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-sm pr-24 sm:pr-28 lg:pr-0">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-nsp-yellow" strokeWidth={2.2} />
                    </div>
                </div>
                <h3 className="mt-5 font-display uppercase text-2xl md:text-4xl tracking-tight leading-none">
                    {title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/70 leading-relaxed">
                    {subtitle}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-nsp-yellow">
                    Scopri
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform duration-300" />
                </div>
            </div>
        </Link>
    );
};

/* Card più piccole, chiare, con sfondo tinta / bianco. */
const HubCardTinted = ({ to, onClick, title, subtitle, icon: Icon, span, variant, badge, highlight, image, chips }) => {
    const variantMap = {
        yellow:
            "bg-gradient-to-br from-nsp-yellow via-nsp-yellow to-amber-400 text-nsp-green border-transparent ring-1 ring-amber-500/30 hover:shadow-[0_20px_40px_-15px_rgba(250,204,21,0.5)]",
        green:
            "bg-gradient-to-br from-nsp-green via-nsp-green-800 to-nsp-green text-white border-transparent ring-1 ring-black/10 hover:shadow-[0_20px_40px_-15px_rgba(10,59,37,0.6)]",
        light:
            "bg-gradient-to-b from-white to-gray-50 text-nsp-green border-gray-100 ring-1 ring-gray-200/70 hover:border-nsp-green-100",
        gray:
            "bg-gradient-to-br from-gray-100 via-gray-50 to-gray-200 text-nsp-green border-2 border-nsp-green ring-2 ring-nsp-green/15 ring-offset-1 hover:shadow-[0_20px_40px_-15px_rgba(10,59,37,0.35)]",
    };
    const iconBoxMap = {
        yellow: "bg-nsp-green text-nsp-yellow",
        green: "bg-nsp-yellow text-nsp-green",
        light: "bg-nsp-green-50 text-nsp-green",
        gray: "bg-nsp-green text-nsp-yellow",
    };

    const inner = (
        <>
            {image && (
                <div className="absolute -right-2 -bottom-6 w-40 max-w-[45%] h-44 md:-right-3 md:-bottom-8 md:w-52 md:h-56 pointer-events-none">
                    <img
                        src={image}
                        alt=""
                        className="w-full h-full object-contain object-right-bottom group-hover:scale-105 transition-transform duration-700"
                    />
                </div>
            )}
            <div className="flex items-start justify-between gap-3">
                <div
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBoxMap[variant]}`}
                >
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <ArrowUpRight className="w-4 h-4 opacity-30 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-300" />
            </div>
            <div className={`mt-6 relative z-10 ${image ? "pr-20 md:pr-28" : ""}`}>
                <h3 className="font-display uppercase text-lg md:text-xl tracking-tight leading-tight">
                    {title}
                </h3>
                <p
                    className={`mt-1 text-xs md:text-sm ${
                        variant === "yellow"
                            ? "text-nsp-green/70"
                            : variant === "green"
                              ? "text-white/70"
                              : variant === "gray"
                                ? "text-gray-600"
                                : "text-gray-500"
                    }`}
                >
                    {subtitle}
                </p>
                {(badge || chips || highlight) && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        {highlight && (
                            <span
                                data-testid="corsi-next-date"
                                className="inline-flex bg-nsp-yellow text-nsp-green text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm"
                            >
                                {highlight}
                            </span>
                        )}
                        {badge && (
                            <span className="inline-flex bg-nsp-green text-nsp-yellow text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                                {badge}
                            </span>
                        )}
                        {chips &&
                            chips.map((c) => (
                                <span
                                    key={c}
                                    className="inline-flex bg-white/80 border border-nsp-green/20 text-nsp-green text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                                >
                                    {c}
                                </span>
                            ))}
                    </div>
                )}
            </div>
        </>
    );

    const cls = `group relative overflow-hidden rounded-3xl p-5 md:p-6 min-h-[170px] md:min-h-[185px] flex flex-col justify-between border shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:-translate-y-1 transition-transform text-left ${variantMap[variant]} ${span}`;

    if (onClick) {
        return (
            <button type="button" onClick={onClick} data-testid="hub-card-chat" className={cls}>
                {inner}
            </button>
        );
    }
    return (
        <Link to={to} data-testid={`hub-card-${to.slice(1)}`} className={cls}>
            {inner}
        </Link>
    );
};

const ComfortCard = ({ icon: Icon, title, text, href }) => {
    const inner = (
        <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-nsp-green-100">
                <Icon className="w-5 h-5 text-nsp-green" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
                <div className="font-bold text-nsp-green text-sm md:text-base">{title}</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1">{text}</div>
            </div>
        </div>
    );
    return href ? (
        <Link to={href} className="hover:opacity-80 transition-opacity">
            {inner}
        </Link>
    ) : (
        inner
    );
};

export default HomePage;
